import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts'
import { PieChart, Wallet } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

const COLORS = ['#0F766E', '#0EA5E9', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#10B981', '#6366F1']

export default function DashboardFonteTab({ dashboardFonte }) {
  const hasData = dashboardFonte && dashboardFonte.por_fonte?.length > 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-teal-600" />
            Valores Aprovados por Fonte de Recurso
          </CardTitle>
          <CardDescription>Distribuição dos valores aprovados por fonte</CardDescription>
        </CardHeader>
        <CardContent>
          {hasData ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={dashboardFonte.por_fonte}
                    cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3}
                    dataKey="valor_total" nameKey="fonte"
                    label={({ fonte, valor_total }) => `${fonte}: ${formatCurrency(valor_total)}`}
                  >
                    {dashboardFonte.por_fonte.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-72 text-slate-400">
              <PieChart className="w-12 h-12 mb-4" />
              <p>Nenhum processo aprovado</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-teal-600" />
            Quantidade por Fonte de Recurso
          </CardTitle>
          <CardDescription>Número de processos aprovados por fonte</CardDescription>
        </CardHeader>
        <CardContent>
          {hasData ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dashboardFonte.por_fonte} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="fonte" type="category" width={100} />
                  <RechartsTooltip
                    formatter={(value, name) => [name === 'quantidade' ? value : formatCurrency(value), name === 'quantidade' ? 'Quantidade' : 'Valor']}
                  />
                  <Bar dataKey="quantidade" fill="#0F766E" name="Quantidade" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-72 text-slate-400">
              <Wallet className="w-12 h-12 mb-4" />
              <p>Nenhum processo aprovado</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Resumo por Fonte de Recurso</CardTitle>
          <CardDescription>
            Total aprovado: {formatCurrency(dashboardFonte?.total_aprovado || 0)} em {dashboardFonte?.total_processos || 0} processo(s)
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {hasData ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Fonte de Recurso</TableHead>
                  <TableHead className="text-center">Quantidade</TableHead>
                  <TableHead className="text-right">Valor Total</TableHead>
                  <TableHead className="text-right">% do Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboardFonte.por_fonte.map((item, index) => (
                  <TableRow key={item.fonte}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span className="font-medium">{item.fonte}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{item.quantidade}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(item.valor_total)}</TableCell>
                    <TableCell className="text-right">
                      {dashboardFonte.total_aprovado > 0
                        ? `${((item.valor_total / dashboardFonte.total_aprovado) * 100).toFixed(1)}%`
                        : '0%'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-slate-400">
              <p>Nenhum dado disponível</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
